import { ref, type Ref } from 'vue'

import { selectableFieldsApi } from '../api/SelectableFieldsApi'
import type { ReportColumn } from '../models/column.types'
import type { SelectableField } from '../models/selectableFields.types'
import { mapColumns } from '../service/mapColumns'

import { useMetricCatalog } from './useMetricCatalog'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError } from '@/shared/http'

export interface SelectableFieldColumns {
  dimensions: ReportColumn[]
  metrics: ReportColumn[]
}

export interface UseSelectableFields {
  dimensions: Ref<ReportColumn[]>
  metrics: Ref<ReportColumn[]>
  isLoading: Ref<boolean>
  error: Ref<ApiError | null>
  /** partitionName comes from the selected level, never a hardcoded value. */
  load: (partitionName: string | undefined) => Promise<SelectableFieldColumns>
}

/** Legacy parity (`decorateFields`): numeric/non-numeric is decided by which half of the
 *  response a field came from, never by the catalog's own guess. */
function joinColumns(
  fields: readonly SelectableField[],
  numeric: boolean,
  getColumn: (field: string) => ReportColumn | null,
): ReportColumn[] {
  const byField = new Map(fields.map((field) => [field.field, field]))
  return mapColumns(
    fields.map((field) => field.field),
    {
      getColumn,
      fallbackHeader: (field) => byField.get(field)?.visible_name ?? field,
    },
  ).map((column) => ({ ...column, numeric: column.numeric ?? numeric }))
}

/**
 * Which fields a workspace can actually offer for one report partition — the live, workspace-
 * scoped counterpart to `useLevelMetadata`'s bootstrap-driven static level catalog, which can
 * list zero fields for a partition a workspace genuinely has data for. Alerts' "Group by"/"Track
 * these metrics" pickers need this source; the level picker itself keeps reading the static one.
 */
export function useSelectableFields(): UseSelectableFields {
  const account = useAccount()
  const workspace = useWorkspace()
  const catalog = useMetricCatalog()

  const dimensions = ref<ReportColumn[]>([])
  const metrics = ref<ReportColumn[]>([])
  const isLoading = ref(false)
  const error = ref<ApiError | null>(null)

  async function load(partitionName: string | undefined): Promise<SelectableFieldColumns> {
    dimensions.value = []
    metrics.value = []
    error.value = null
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    if (accountId === undefined || workspaceId === undefined || !partitionName) {
      return { dimensions: [], metrics: [] }
    }

    isLoading.value = true
    try {
      const response = await selectableFieldsApi.fetchSelectableFields(
        accountId,
        workspaceId,
        partitionName,
      )
      // An empty list is a valid terminal answer: the workspace has no tab on this partition.
      dimensions.value = joinColumns(response.dimensions, false, catalog.getColumn)
      metrics.value = joinColumns(response.metrics, true, catalog.getColumn)
      return { dimensions: dimensions.value, metrics: metrics.value }
    } catch (cause) {
      error.value = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      return { dimensions: [], metrics: [] }
    } finally {
      isLoading.value = false
    }
  }

  return { dimensions, metrics, isLoading, error, load }
}
